import { useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Unstable_Grid2 as Grid } from '@mui/material';

import { usePlan } from '../../../context/WorkPlanContext';
import { useMember } from '../../../context/MemberContext';
import { useTeam } from '../../../context/TeamContext';

import Header from '../../../components/Header';
import MultipleSelect from '../../../components/MultipleSelect';
import ProjectCalendarTimeline from '../../../components/CalendarTimeline';

import WorkLogDialog from './WorkLogDialog';
import MemberTaskItem from './MemberTaskItem';

const ProjectDetails = () => {
  const navigate = useNavigate();
  const { id: projectId } = useParams();
  const { teamsExcludeSettings } = useTeam();
  const { members } = useMember();
  const { plansByProject, fetchPlansByProject } = usePlan();

  const [openDialog, setOpenDialog] = useState(false);
  const [editWorkLog, setEditWorkLog] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const calendarRef = useRef(null);

  const { teamLoggedEvents, teamResources, project } = useMemo(() => {
    const result = plansByProject.reduce(
      (resume, { data, project }) => {
        resume.project = project;

        data.forEach(({ day, teams }) => {
          if (!teams.length) return;

          teams.forEach(({ members, team }) => {
            if (
              selectedTeamIds.length > 0 &&
              !selectedTeamIds.includes(team._id)
            )
              return;

            if (!resume.teamResources[team._id]) {
              resume.teamResources[team._id] = {
                id: team._id,
                title: team.name,
              };
            }

            members.forEach((member) => {
              if (
                selectedMemberIds.length > 0 &&
                !selectedMemberIds.includes(member._id)
              )
                return;

              resume.teamLoggedEvents.push({
                id: member._id,
                title: member.full_name || '',
                resourceId: team._id,
                start: day,
                end: day,
                member,
              });
            });
          });
        });

        return resume;
      },
      {
        teamLoggedEvents: [],
        teamResources: {},
        project: {},
      }
    );

    return result;
  }, [plansByProject, selectedTeamIds, selectedMemberIds]);

  const memberByTeam = useMemo(() => {
    const mems =
      selectedTeamIds.length > 0
        ? members.filter((item) => selectedTeamIds.includes(item.team))
        : members;
    return mems.map(({ id, full_name: name }) => ({ id, name }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, selectedTeamIds]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleDateClick = (selected) => {
    setSelectedDate(selected);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditWorkLog(null); // Reset edit event when closing the dialog
  };

  const onChangeRangeDates = async ({ start_day, end_day }) => {
    try {
      await fetchPlansByProject({
        project: projectId,
        start_day,
        end_day,
      });
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const renderEventContent = (eventInfo) => {
    return <MemberTaskItem eventInfo={eventInfo} />;
  };

  return (
    <Box m="20px">
      <Header
        title={`Project: ${project?.name || '-'}`}
        subtitle={`PL: ${project?.project_lead?.full_name || '-'}`}
        onGoBack={handleGoBack}
      />
      <Box display="flex" justifyContent="flex-end" gap={1} mb={1}>
        <MultipleSelect
          options={teamsExcludeSettings}
          selectedOption={selectedTeamIds}
          onChangeOptions={setSelectedTeamIds}
          allText="All team"
        />
        <MultipleSelect
          options={memberByTeam}
          selectedOption={selectedMemberIds}
          onChangeOptions={setSelectedMemberIds}
          allText="All member"
        />
      </Box>
      <Grid container spacing={2}>
        <Grid xs={12}>
          <Box ml="15px">
            <ProjectCalendarTimeline
              resources={Object.values(teamResources)}
              events={teamLoggedEvents}
              ref={calendarRef}
              onDateClick={handleDateClick}
              onChangeRangeDates={onChangeRangeDates}
              renderEventContent={renderEventContent}
              adjustHeight={180}
            />
          </Box>
        </Grid>
      </Grid>

      <WorkLogDialog
        isOpen={openDialog}
        onClose={handleCloseDialog}
        selectedEvent={editWorkLog}
        selectedDate={selectedDate}
      />
    </Box>
  );
};

export default ProjectDetails;
